import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';

import { isPlainObject } from 'lodash';

import { Observable, observableOf, rx, observableEMPTY } from '@shared/rx';
import { SyncOrAsync } from '@shared/types';
import { asObservable, isAsync, safeKey } from '@shared/utils';

export interface IValueMakers<R> {
  withDefaultValue: (control: AbstractControl) => Observable<R>;
  noDefaultValue: (control: AbstractControl) => Observable<R>;
}

interface IConfig<T> {
  hasInitialValue?: boolean;
  defaultIfNoControl?: T;
}

export interface IConfigWithPath<T> extends IConfig<T> {
  path?: FormPath;
}

interface IConfigInternal<T> extends IConfigWithPath<T> {
  valueMakers: IValueMakers<T>;
}

type FormPath = number | string | (number | string)[];

const defaultConfig: IConfigWithPath<any> = {
  hasInitialValue: true,
};

const buildConfig = <T>(
  configOrPath: FormPath | IConfig<T> | IConfigWithPath<T>,
  valueMakers: IValueMakers<T>,
): IConfigInternal<T> =>
  isPlainObject(configOrPath)
    ? {
        ...defaultConfig,
        ...(configOrPath as IConfig<T> | IConfigWithPath<T>),
        valueMakers,
      }
    : { ...defaultConfig, valueMakers, path: configOrPath as FormPath };

export interface IOnControlChangedOperator<TBaseValueType> {
  /* eslint-disable max-len */
  <R extends TBaseValueType>(
    form: SyncOrAsync<FormControl>,
    config?: IConfig<R>,
  ): Observable<R>;
  <R extends TBaseValueType>(
    form: SyncOrAsync<FormGroup | FormArray>,
    configOrPath?: FormPath | IConfigWithPath<R>,
  ): Observable<R>;
  <R extends TBaseValueType>(
    form: SyncOrAsync<AbstractControl>,
    configOrPath?: FormPath | IConfigWithPath<R> | IConfig<R>,
  ): Observable<R>;
  <R extends TBaseValueType>(
    configOrPath: FormPath | IConfigWithPath<R>,
  ): (o$: Observable<FormGroup | FormArray>) => Observable<R>;
  <R extends TBaseValueType>(
    config?: IConfig<R>,
  ): (o$: Observable<FormControl>) => Observable<R>;
  /* eslint-enable max-len */
}

// eslint-disable-next-line
export const makeOnControlChangedOperator =
  <T>(makers: IValueMakers<T>): IOnControlChangedOperator<T> =>
  // TODO: Improve Typings after update to TS3.4+
  (...args: any[]): any => {
    const isExecutedAsAnRxCreator =
      args.length >= 1 &&
      (args[0] instanceof AbstractControl || isAsync(args[0]));

    if (isExecutedAsAnRxCreator) {
      const form: SyncOrAsync<AbstractControl> = args[0];

      return makeStream(buildConfig<T>(args[1], makers), form);
    }

    return makeStream.bind(undefined, buildConfig(args[0], makers));
  };

const makeStream = <R>(
  config: IConfigInternal<R>,
  formOrAsyncForm: SyncOrAsync<AbstractControl>,
): Observable<R> => {
  const valueOfControl = (control: AbstractControl | null): Observable<R> => {
    if (control) {
      return config.hasInitialValue
        ? config.valueMakers.withDefaultValue(control)
        : config.valueMakers.noDefaultValue(control);
    }

    return config.hasOwnProperty(safeKey<IConfig<any>>('defaultIfNoControl'))
      ? observableOf(config.defaultIfNoControl)
      : observableEMPTY;
  };

  const value$: Observable<R> = asObservable(formOrAsyncForm).pipe(
    rx.filter(Boolean),
    rx.map((form: AbstractControl) =>
      config.path ? form.get(config.path as string | string[]) : form,
    ),
    rx.switchMap((control: AbstractControl | null) =>
      valueOfControl(control).pipe(rx.distinctUntilChanged()),
    ),
  );

  return value$;
};
